// FIX-CCS-OUTBOX (spec §7.5) — what the farmer sees: the status banner (no signal, sign in to send,
// offline sending not enabled, expired photos, other accounts' photos, waiting + Send now, iPhone tip)
// and the pending-photo grid (greyed with a clock; failed with a warning and Retry). All strings are
// translated (outbox.* keys); nothing uses a native alert().

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Clock, CloudOff, LogIn, RotateCw, Send, Smartphone, Users } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { useAuth } from '@/features/auth/components/AuthContext';
import { cn } from '@/shared/lib/utils';
import { useOutbox, type OutboxPhotoView } from './OutboxProvider';

const Row: React.FC<{ icon: React.ReactNode; tone?: 'info' | 'warn'; children: React.ReactNode; action?: React.ReactNode; testId: string }> = ({
  icon,
  tone = 'info',
  children,
  action,
  testId,
}) => (
  <div
    data-testid={testId}
    role="status"
    className={cn(
      'flex items-center gap-3 rounded-xl border px-3 py-2 text-sm',
      tone === 'warn' ? 'border-warning/40 bg-warning/10 text-foreground' : 'border-border bg-muted/60 text-foreground',
    )}
  >
    <span className="shrink-0">{icon}</span>
    <span className="flex-1">{children}</span>
    {action}
  </div>
);

/** The outbox status lines. Renders nothing when there is nothing to say. */
export const OutboxBanner: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const ob = useOutbox();
  const { summary } = ob;
  const mineWaiting = summary.pending;

  const rows: React.ReactNode[] = [];
  if (ob.enabled && !ob.online) {
    rows.push(
      <Row key="offline" testId="outbox-offline" icon={<CloudOff className="h-5 w-5" />}>
        {t('outbox.offlineBanner')}
      </Row>,
    );
  }
  if (ob.notice === 'auth_needed' && mineWaiting > 0) {
    rows.push(
      <Row
        key="auth"
        testId="outbox-auth-needed"
        tone="warn"
        icon={<LogIn className="h-5 w-5" />}
        action={
          <Button
            size="sm"
            onClick={async () => {
              // R6: the outbox is kept; only the session is cleared. After the PIN the runner resumes (login trigger).
              await logout();
              navigate('/auth');
            }}
          >
            {t('outbox.signIn')}
          </Button>
        }
      >
        {t('outbox.signInToSend', { count: mineWaiting })}
      </Row>,
    );
  }
  if (ob.notice === 'offline_disabled' && mineWaiting > 0) {
    rows.push(
      <Row key="disabled" testId="outbox-offline-disabled" tone="warn" icon={<AlertTriangle className="h-5 w-5" />}>
        {t('outbox.offlineDisabled', { count: mineWaiting })}
      </Row>,
    );
  }
  if (ob.expiredCount > 0) {
    rows.push(
      <Row
        key="expired"
        testId="outbox-expired"
        tone="warn"
        icon={<Clock className="h-5 w-5" />}
        action={
          <Button size="sm" variant="outline" onClick={() => ob.acknowledgeExpired()}>
            {t('outbox.ok')}
          </Button>
        }
      >
        {t('outbox.expired', { count: ob.expiredCount })}
      </Row>,
    );
  }
  if (summary.otherAccounts > 0) {
    rows.push(
      <Row key="others" testId="outbox-other-accounts" icon={<Users className="h-5 w-5" />}>
        {t('outbox.otherAccount', { count: summary.otherAccounts })}
      </Row>,
    );
  }
  if (mineWaiting > 0 && ob.online && ob.notice === null) {
    rows.push(
      <Row
        key="waiting"
        testId="outbox-waiting"
        icon={<Clock className="h-5 w-5" />}
        action={
          <Button size="sm" variant="outline" disabled={ob.running} onClick={() => ob.run('send_now', { force: true })}>
            <Send className="mr-1 h-4 w-4" />
            {ob.running ? t('outbox.sending') : t('outbox.sendNow')}
          </Button>
        }
      >
        {t('outbox.pendingCount', { count: mineWaiting })}
      </Row>,
    );
  }
  if (ob.showIosTip) {
    rows.push(
      <Row
        key="ios"
        testId="outbox-ios-tip"
        icon={<Smartphone className="h-5 w-5" />}
        action={
          <Button size="sm" variant="outline" onClick={ob.dismissIosTip}>
            {t('outbox.ok')}
          </Button>
        }
      >
        {t('outbox.iosTip')}
      </Row>,
    );
  }
  if (rows.length === 0) return null;
  return <div className={cn('space-y-2', className)}>{rows}</div>;
};

const LOCATION_EMOJI: Record<OutboxPhotoView['location'], string> = { verified: '📍', unverified: '🕒', none: '❔', pending: '⏳' };

/** The signed-in farmer's pending (greyed, clock) and failed (warning, Retry) photos. */
export const OutboxPendingGrid: React.FC<{ columns?: 2 | 3; className?: string }> = ({ columns = 3, className }) => {
  const { t } = useTranslation();
  const ob = useOutbox();
  if (ob.photos.length === 0) return null;
  return (
    <div className={className} data-testid="outbox-pending-grid">
      <p className="mb-2 text-sm font-medium text-muted-foreground">{t('outbox.pendingLabel')}</p>
      <div className={cn('grid gap-1', columns === 2 ? 'grid-cols-2' : 'grid-cols-3')}>
        {ob.photos.map((p) => (
          <div
            key={p.photoId}
            data-testid={`outbox-item-${p.state}`}
            data-photo-id={p.photoId}
            className="relative aspect-square overflow-hidden rounded-lg bg-muted"
          >
            {p.previewUrl ? (
              <img src={p.previewUrl} alt={p.category} className="h-full w-full object-cover opacity-50 grayscale" />
            ) : (
              <div className="h-full w-full" />
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              {p.state === 'failed' ? (
                <div className="flex flex-col items-center gap-1">
                  <AlertTriangle className="h-7 w-7 text-warning drop-shadow" aria-label={t('outbox.failed')} />
                  <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={() => ob.retry(p.photoId)}>
                    <RotateCw className="mr-1 h-3 w-3" />
                    {t('outbox.retry')}
                  </Button>
                </div>
              ) : (
                <Clock className="h-7 w-7 text-white drop-shadow" aria-label={t('outbox.pendingLabel')} />
              )}
            </div>
            {/* G-A unit 4 (R2): the location this queued photo carries — always labelled */}
            <span
              data-testid="outbox-item-location"
              data-pick={p.location}
              className="absolute bottom-1 left-1 right-1 truncate rounded bg-black/60 px-1 text-[10px] leading-4 text-white"
            >
              {LOCATION_EMOJI[p.location]} {t(`location.pick.${p.location}`)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/** The count for badges (camera button, Photos buttons): the farmer's own waiting + failed photos. */
export function useOutboxBadgeCount(): number {
  const { summary } = useOutbox();
  return summary.pending + summary.failed;
}
